import { useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { FiUpload } from 'react-icons/fi'
import { useHttpMethods } from '../../../Common/Hooks/useApi/HttpMethods'
import { isErr } from '../../../Common/Utils/Result'
import { BRAND_GREEN } from '@dd-report-components'

const BRAND_RED = '#dc2626'

export interface PdfSettings {
  taxId: string | null
  contactData: string | null
  companyHeadquarters: string | null
  closingDisclaimerText: string
  footerText: string | null
  logoPreviewUrl: string | null
  signerCity: string | null
  signerName: string | null
  signerRole: string | null
}

export interface PdfSettingsSaveState {
  isSaving: boolean
  savedVisible: boolean
  savedFading: boolean
  saveError: string | null
}

interface PdfSettingsFormProps {
  // API path this form reads from and saves to, e.g. '/pdf-settings' for the
  // business-wide config or '/due-diligence/cases/:id/pdf-settings' for a
  // per-case override — the fields and save behavior are identical either way.
  basePath: string
  // Lets a wrapper (e.g. the modal header) mirror the save status shown next
  // to the CIF/NIF field, since that state lives inside this component.
  onSaveStateChange?: (state: PdfSettingsSaveState) => void
}

const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`

const FormSection = styled.div`
  margin-bottom: 1.5rem;
`

const SectionLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
`

const StyledTextInput = styled.input`
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  outline: none;
  color: #111827;
  background: #ffffff;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${BRAND_GREEN};
  }
`

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  outline: none;
  color: #111827;
  background: #ffffff;
  box-sizing: border-box;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${BRAND_GREEN};
  }
`

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

export const SaveIndicator = styled.div<{ $visible: boolean; $fading: boolean; $error?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: ${(props) => (props.$error ? BRAND_RED : props.$fading ? BRAND_GREEN : '#6b7280')};
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  animation: ${(props) => (props.$error ? 'none' : props.$fading ? fadeOut : 'none')} 1s ease forwards;
  transition: opacity 0.2s;
  min-width: 80px;
`

const LogoErrorHint = styled.p`
  font-size: 0.75rem;
  color: ${BRAND_RED};
  margin: 0.5rem 0 0;
`

export const SpinnerCircle = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`

const LogoZone = styled.div<{ $isDragging: boolean }>`
  border: 2px dashed ${(props) => (props.$isDragging ? BRAND_GREEN : '#d1d5db')};
  border-radius: 0.5rem;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  background: ${(props) => (props.$isDragging ? 'rgba(46, 184, 138, 0.05)' : '#f9fafb')};
  transition: all 0.2s;

  &:hover {
    border-color: ${BRAND_GREEN};
    background: rgba(46, 184, 138, 0.04);
  }
`

const LogoPreview = styled.img`
  max-height: 80px;
  max-width: 200px;
  object-fit: contain;
  border-radius: 4px;
  margin-bottom: 0.5rem;
`

const LogoUploadHint = styled.p`
  font-size: 0.75rem;
  color: #9ca3af;
  margin: 0.25rem 0 0;
`

const LogoUploadIcon = styled.div`
  color: #9ca3af;
  margin-bottom: 0.5rem;
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #f3f4f6;
  margin: 0.25rem 0 1.5rem;
`

const SignaturePreview = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 1rem 1.25rem;
  font-size: 0.8rem;
  line-height: 1.8;
  color: #374151;
  white-space: pre-wrap;
  font-style: italic;
`

const PreviewLabel = styled.p`
  font-size: 0.7rem;
  color: #9ca3af;
  margin: 0.5rem 0 0.375rem;
`

export const PdfSettingsForm = ({ basePath, onSaveStateChange }: PdfSettingsFormProps) => {
  const { t } = useTranslation()
  const { get, put, postWithFormData } = useHttpMethods()

  const [settings, setSettings] = useState<PdfSettings>({
    taxId: null,
    contactData: null,
    companyHeadquarters: null,
    closingDisclaimerText: '',
    footerText: null,
    logoPreviewUrl: null,
    signerCity: null,
    signerName: null,
    signerRole: null,
  })

  const [isSaving, setIsSaving] = useState(false)
  const [savedVisible, setSavedVisible] = useState(false)
  const [savedFading, setSavedFading] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [logoError, setLogoError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const fetchSettings = async () => {
      const result = await get<{ settings: PdfSettings }>(basePath)
      if ('value' in result && result.value) {
        setSettings(result.value.settings)
      }
    }
    fetchSettings()
  }, [basePath])

  useEffect(() => {
    onSaveStateChange?.({ isSaving, savedVisible, savedFading, saveError })
  }, [isSaving, savedVisible, savedFading, saveError])

  const showSaved = () => {
    setSaveError(null)
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    setSavedVisible(true)
    setSavedFading(false)
    savedTimerRef.current = setTimeout(() => {
      setSavedFading(true)
      savedTimerRef.current = setTimeout(() => {
        setSavedVisible(false)
        setSavedFading(false)
      }, 1000)
    }, 2000)
  }

  const showSaveError = (message: string) => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    setSavedVisible(false)
    setSavedFading(false)
    setSaveError(message)
  }

  const handleBlur = async () => {
    setIsSaving(true)
    const result = await put(basePath, {
      taxId: settings.taxId,
      contactData: settings.contactData,
      companyHeadquarters: settings.companyHeadquarters,
      closingDisclaimerText: settings.closingDisclaimerText,
      footerText: settings.footerText,
      signerCity: settings.signerCity,
      signerName: settings.signerName,
      signerRole: settings.signerRole,
    })
    setIsSaving(false)
    if (isErr(result)) {
      showSaveError(result.error.message || t('common.sidebar.pdfSettings.form.saveStatus.genericSaveError'))
      return
    }
    showSaved()
  }

  const signerPreview = (() => {
    const today = new Date()
    const day = today.getDate()
    const months = t('common.sidebar.pdfSettings.form.preview.months', { returnObjects: true }) as string[]
    const month = months[today.getMonth()]
    const year = today.getFullYear()
    const parts: string[] = []
    if (settings.closingDisclaimerText) parts.push(`[izquierda]\n${settings.closingDisclaimerText}`)
    const sigParts: string[] = []
    if (settings.signerCity) sigParts.push(t('common.sidebar.pdfSettings.form.preview.locationDate', { city: settings.signerCity, day, month, year }))
    if (settings.signerName) sigParts.push(t('common.sidebar.pdfSettings.form.preview.signedBy', { name: settings.signerName }))
    if (settings.signerRole) sigParts.push(settings.signerRole)
    if (sigParts.length) parts.push(`[derecha]\n${sigParts.join('\n\n')}`)
    return parts.join('\n\n') || t('common.sidebar.pdfSettings.form.preview.noData')
  })()

  const handleLogoFile = async (file: File) => {
    setIsUploadingLogo(true)
    setLogoError(null)
    const formData = new FormData()
    formData.append('logo', file)
    const result = await postWithFormData<{ previewUrl: string }>(
      `${basePath}/logo`,
      formData
    )
    if (isErr(result)) {
      setLogoError(result.error.message || t('common.sidebar.pdfSettings.form.saveStatus.logoUploadError'))
    } else {
      setSettings((prev) => ({ ...prev, logoPreviewUrl: result.value.previewUrl }))
    }
    setIsUploadingLogo(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) handleLogoFile(file)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleLogoFile(file)
  }

  return (
    <>
      {/* Logo */}
      <FormSection>
        <SectionLabel>{t('common.sidebar.pdfSettings.form.logo.label')}</SectionLabel>
        <LogoZone
          $isDragging={isDragging}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          {settings.logoPreviewUrl ? (
            <>
              <LogoPreview src={settings.logoPreviewUrl} alt={t('common.sidebar.pdfSettings.form.logo.alt')} />
              <LogoUploadHint>{t('common.sidebar.pdfSettings.form.logo.changeHint')}</LogoUploadHint>
            </>
          ) : (
            <>
              <LogoUploadIcon>
                <FiUpload size={24} />
              </LogoUploadIcon>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                {isUploadingLogo ? t('common.sidebar.pdfSettings.form.logo.uploading') : t('common.sidebar.pdfSettings.form.logo.dropHint')}
              </p>
              <LogoUploadHint>{t('common.sidebar.pdfSettings.form.logo.formatHint')}</LogoUploadHint>
            </>
          )}
        </LogoZone>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        {logoError && <LogoErrorHint>{logoError}</LogoErrorHint>}
      </FormSection>

      <Divider />

      {/* CIF / NIF */}
      <FormSection>
        <SectionLabel>{t('common.sidebar.pdfSettings.form.taxId.label')}</SectionLabel>
        <FieldRow>
          <StyledTextInput
            style={{ maxWidth: '160px' }}
            value={settings.taxId ?? ''}
            onChange={(e) => setSettings((prev) => ({ ...prev, taxId: e.target.value || null }))}
            onBlur={handleBlur}
            placeholder={t('common.sidebar.pdfSettings.form.taxId.placeholder')}
          />
          <SaveIndicator
            $visible={savedVisible || isSaving || !!saveError}
            $fading={savedFading}
            $error={!!saveError}
          >
            {isSaving ? (
              <SpinnerCircle />
            ) : (
              <span style={{ color: saveError ? BRAND_RED : BRAND_GREEN }}>
                {saveError ? '✕' : '✓'}
              </span>
            )}
            {isSaving ? t('common.sidebar.pdfSettings.form.saveStatus.saving') : saveError || t('common.sidebar.pdfSettings.form.saveStatus.saved')}
          </SaveIndicator>
        </FieldRow>
      </FormSection>

      {/* Datos de contacto */}
      <FormSection>
        <SectionLabel>{t('common.sidebar.pdfSettings.form.contactData.label')}</SectionLabel>
        <StyledTextarea
          value={settings.contactData ?? ''}
          onChange={(e) => setSettings((prev) => ({ ...prev, contactData: e.target.value || null }))}
          onBlur={handleBlur}
          placeholder={t('common.sidebar.pdfSettings.form.contactData.placeholder')}
          rows={3}
        />
      </FormSection>

      {/* Sede social */}
      <FormSection>
        <SectionLabel>{t('common.sidebar.pdfSettings.form.companyHeadquarters.label')}</SectionLabel>
        <StyledTextInput
          value={settings.companyHeadquarters ?? ''}
          onChange={(e) => setSettings((prev) => ({ ...prev, companyHeadquarters: e.target.value || null }))}
          onBlur={handleBlur}
          placeholder={t('common.sidebar.pdfSettings.form.companyHeadquarters.placeholder')}
        />
      </FormSection>

      {/* Pie de página */}
      <FormSection>
        <SectionLabel>{t('common.sidebar.pdfSettings.form.footerText.label')}</SectionLabel>
        <StyledTextInput
          value={settings.footerText ?? ''}
          onChange={(e) => setSettings((prev) => ({ ...prev, footerText: e.target.value || '' }))}
          onBlur={handleBlur}
          placeholder={t('common.sidebar.pdfSettings.form.footerText.placeholder')}
        />
      </FormSection>

      {/* Texto de cierre / disclaimer */}
      <FormSection>
        <SectionLabel>{t('common.sidebar.pdfSettings.form.closingDisclaimerText.label')}</SectionLabel>
        <StyledTextarea
          value={settings.closingDisclaimerText ?? ''}
          onChange={(e) => setSettings((prev) => ({ ...prev, closingDisclaimerText: e.target.value || '' }))}
          onBlur={handleBlur}
          rows={8}
        />
      </FormSection>

      {/* Datos del firmante */}
      <FormSection>
        <SectionLabel>{t('common.sidebar.pdfSettings.form.signerCity.label')}</SectionLabel>
        <StyledTextInput
          style={{ maxWidth: '200px' }}
          value={settings.signerCity ?? ''}
          onChange={(e) => setSettings((prev) => ({ ...prev, signerCity: e.target.value || null }))}
          onBlur={handleBlur}
          placeholder={t('common.sidebar.pdfSettings.form.signerCity.placeholder')}
        />
      </FormSection>

      <FormSection>
        <SectionLabel>{t('common.sidebar.pdfSettings.form.signerName.label')}</SectionLabel>
        <StyledTextInput
          value={settings.signerName ?? ''}
          onChange={(e) => setSettings((prev) => ({ ...prev, signerName: e.target.value || null }))}
          onBlur={handleBlur}
          placeholder={t('common.sidebar.pdfSettings.form.signerName.placeholder')}
        />
      </FormSection>

      <FormSection>
        <SectionLabel>{t('common.sidebar.pdfSettings.form.signerRole.label')}</SectionLabel>
        <StyledTextInput
          value={settings.signerRole ?? ''}
          onChange={(e) => setSettings((prev) => ({ ...prev, signerRole: e.target.value || null }))}
          onBlur={handleBlur}
          placeholder={t('common.sidebar.pdfSettings.form.signerRole.placeholder')}
        />
      </FormSection>

      <PreviewLabel>{t('common.sidebar.pdfSettings.form.preview.label')}</PreviewLabel>
      <SignaturePreview>{signerPreview}</SignaturePreview>
    </>
  )
}
